import { useEffect, useState } from "react";
import { toast } from "sonner";
import { X, ChevronLeft, ChevronRight, Download, GitCompare, Star, Trash2 } from "lucide-react";
import api from "@/lib/api";
import { TAG_PALETTE } from "@/lib/tagPalette";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

function PhotoLightbox({ photos, index, onClose, onIndex }) {
    useEffect(() => {
        if (index === null || index === undefined) return;
        function onKey(e) {
            if (e.key === "Escape") onClose();
            if (e.key === "ArrowLeft") onIndex((i) => Math.max(0, i - 1));
            if (e.key === "ArrowRight") onIndex((i) => Math.min(photos.length - 1, i + 1));
        }
        window.addEventListener("keydown", onKey);
        return () => window.removeEventListener("keydown", onKey);
    }, [photos.length, index, onClose, onIndex]);
    if (index === null || index === undefined) return null;
    const src = `${BACKEND_URL}/api/uploads/${photos[index]}`;
    return (
        <div className="fixed inset-0 z-[60] bg-black/95 flex items-center justify-center" onClick={onClose} data-testid="photo-lightbox">
            <button onClick={(e) => { e.stopPropagation(); onClose(); }} className="absolute top-6 right-6 text-white/70 hover:text-white p-2" data-testid="lightbox-close" aria-label="Close"><X size={28} /></button>
            <button onClick={(e) => { e.stopPropagation(); onIndex((i) => Math.max(0, i - 1)); }} disabled={index === 0} className="absolute left-4 md:left-8 text-white/70 hover:text-white disabled:opacity-25 p-3" data-testid="lightbox-prev" aria-label="Previous"><ChevronLeft size={40} /></button>
            <img src={src} alt="applicant" onClick={(e) => e.stopPropagation()} className="max-h-[92vh] max-w-[90vw] object-contain" data-testid="lightbox-image" />
            <button onClick={(e) => { e.stopPropagation(); onIndex((i) => Math.min(photos.length - 1, i + 1)); }} disabled={index === photos.length - 1} className="absolute right-4 md:right-8 text-white/70 hover:text-white disabled:opacity-25 p-3" data-testid="lightbox-next" aria-label="Next"><ChevronRight size={40} /></button>
            <p className="absolute bottom-6 left-0 right-0 text-center text-white/60 text-xs uppercase tracking-[0.24em]" data-testid="lightbox-counter">
                {index + 1} / {photos.length} · use ← → arrows · esc to close
            </p>
        </div>
    );
}

function CastingNotes({ app, onSaved }) {
    const [draft, setDraft] = useState("");
    const [busy, setBusy] = useState(false);
    useEffect(() => { setDraft(""); }, [app.id]);
    const entries = (app.notes_log && app.notes_log.length)
        ? app.notes_log
        : (app.admin_notes ? [{ id: "legacy", text: app.admin_notes, saved_at: null }] : []);
    const sorted = [...entries].sort((a, b) => (b.saved_at || "").localeCompare(a.saved_at || ""));

    const add = async () => {
        const text = draft.trim();
        if (!text) return;
        setBusy(true);
        try {
            await api.post(`/admin/applications/${app.id}/notes`, { text });
            setDraft("");
            toast.success("Note added");
            onSaved?.();
        } catch { toast.error("Save failed"); }
        finally { setBusy(false); }
    };

    const remove = async (noteId) => {
        if (!window.confirm("Delete this note?")) return;
        try {
            await api.delete(`/admin/applications/${app.id}/notes/${noteId}`);
            toast.success("Deleted");
            onSaved?.();
        } catch { toast.error("Delete failed"); }
    };

    const fmt = (iso) => {
        if (!iso) return "Earlier note";
        try { return new Date(iso).toLocaleString(); } catch { return iso; }
    };

    return (
        <section className="mt-8" data-testid="detail-notes-section">
            <div className="flex items-baseline justify-between">
                <p className="eyebrow">Private casting notes</p>
                <span className="text-[0.6rem] uppercase tracking-[0.24em] text-[color:var(--cream-muted)]">
                    Admin only · {sorted.length} entr{sorted.length === 1 ? "y" : "ies"}
                </span>
            </div>
            <textarea
                className="input-line w-full mt-3 min-h-[100px]"
                placeholder="Add a timestamped note — impressions, red flags, follow-ups…"
                value={draft}
                onChange={(e) => setDraft(e.target.value)}
                data-testid="detail-notes-textarea"
            />
            <div className="mt-2 flex justify-end">
                <button onClick={add} disabled={busy || !draft.trim()} className="btn-gold btn-gold-solid text-[0.65rem] disabled:opacity-40" data-testid="detail-notes-save">
                    {busy ? "Saving…" : "+ Add note"}
                </button>
            </div>
            {sorted.length > 0 && (
                <div className="mt-6 space-y-4" data-testid="notes-history">
                    {sorted.map((n, i) => (
                        <div key={n.id || i} className="border-l-2 border-[color:var(--gold)]/40 pl-4 py-1" data-testid={`note-entry-${n.id || i}`}>
                            <div className="flex items-baseline justify-between gap-3">
                                <p className="text-[0.65rem] uppercase tracking-[0.24em] text-[color:var(--cream-muted)]">{fmt(n.saved_at)}</p>
                                {n.id && n.id !== "legacy" && (
                                    <button onClick={() => remove(n.id)} className="text-[color:var(--cream-muted)] hover:text-[color:var(--wine)] transition-colors" data-testid={`note-delete-${n.id}`} aria-label="Delete note">
                                        <Trash2 size={12} />
                                    </button>
                                )}
                            </div>
                            <p className="mt-1 text-sm text-[color:var(--soft-cream)] whitespace-pre-wrap">{n.text}</p>
                        </div>
                    ))}
                </div>
            )}
        </section>
    );
}

async function reorderPhotos(appId, keys) {
    await api.post(`/admin/applications/${appId}/photo-order`, { photo_keys: keys });
}

function TagManager({ app, onChanged, allTags = [] }) {
    const [label, setLabel] = useState("");
    const [color, setColor] = useState(TAG_PALETTE[0].hex);
    const [busy, setBusy] = useState(false);
    const tags = app.tags || [];
    const existingOnThisApp = new Set(tags.map((t) => t.label.toLowerCase()));
    const suggestions = allTags
        .filter((t) => !existingOnThisApp.has(t.label.toLowerCase()))
        .filter((t) => !label || t.label.toLowerCase().includes(label.toLowerCase()));

    const pickSuggestion = (t) => { setLabel(t.label); setColor(t.color); };

    const add = async () => {
        const t = label.trim();
        if (!t) return;
        setBusy(true);
        try {
            await api.post(`/admin/applications/${app.id}/tags`, { label: t, color });
            setLabel("");
            toast.success("Tag added");
            onChanged?.();
        } catch { toast.error("Failed to add tag"); }
        finally { setBusy(false); }
    };

    const remove = async (tagId) => {
        try {
            await api.delete(`/admin/applications/${app.id}/tags/${tagId}`);
            onChanged?.();
        } catch { toast.error("Failed to remove tag"); }
    };

    return (
        <section className="mt-8" data-testid="detail-tags-section">
            <p className="eyebrow">Tags</p>
            <div className="mt-3 flex flex-wrap gap-2">
                {tags.map((t) => (
                    <span key={t.id} className="inline-flex items-center gap-1 text-[0.6rem] uppercase tracking-[0.2em] px-2 py-1 border" style={{ borderColor: t.color, color: t.color }} data-testid={`tag-chip-${t.id}`}>
                        {t.label}
                        <button onClick={() => remove(t.id)} className="hover:text-[color:var(--wine)]" data-testid={`tag-remove-${t.id}`} aria-label="Remove tag">
                            <X size={10} />
                        </button>
                    </span>
                ))}
                {tags.length === 0 && <span className="text-xs italic text-[color:var(--cream-muted)]">No tags yet.</span>}
            </div>
            <div className="mt-4 flex flex-wrap items-center gap-2">
                <input
                    className="input-line flex-1 min-w-[180px]"
                    placeholder='e.g. "great on camera"'
                    value={label}
                    onChange={(e) => setLabel(e.target.value)}
                    onKeyDown={(e) => { if (e.key === "Enter") { e.preventDefault(); add(); } }}
                    data-testid="tag-label-input"
                    autoComplete="off"
                />
                <div className="flex gap-1" data-testid="tag-color-swatches">
                    {TAG_PALETTE.map((c) => (
                        <button
                            key={c.name}
                            onClick={() => setColor(c.hex)}
                            className={"w-6 h-6 border-2 transition-transform " + (color === c.hex ? "scale-125" : "border-transparent")}
                            style={{ background: c.hex, borderColor: color === c.hex ? "#f7f4f0" : "transparent" }}
                            data-testid={`tag-color-${c.name}`}
                            aria-label={`Color ${c.name}`}
                            type="button"
                        />
                    ))}
                </div>
                <button onClick={add} disabled={busy || !label.trim()} className="btn-gold btn-gold-solid text-[0.65rem] disabled:opacity-40" data-testid="tag-add-btn">
                    {busy ? "…" : "+ Add tag"}
                </button>
            </div>
            {suggestions.length > 0 && (
                <div className="mt-3" data-testid="tag-suggestions">
                    <p className="text-[0.55rem] uppercase tracking-[0.24em] text-[color:var(--cream-muted)] mb-2">
                        {label ? "Matching existing tags" : "Reuse from other applicants"}
                    </p>
                    <div className="flex flex-wrap gap-1">
                        {suggestions.slice(0, 12).map((t) => (
                            <button
                                key={t.label}
                                type="button"
                                onClick={() => pickSuggestion(t)}
                                className="text-[0.55rem] uppercase tracking-[0.2em] px-2 py-1 border transition-transform hover:scale-105"
                                style={{ borderColor: t.color, color: t.color }}
                                data-testid={`tag-suggestion-${t.label}`}
                            >
                                + {t.label}
                            </button>
                        ))}
                    </div>
                </div>
            )}
        </section>
    );
}

async function bulkExportPortfolio(ids) {
    try {
        const res = await fetch(`${BACKEND_URL}/api/admin/applications/bulk-pdf`, {
            method: "POST",
            credentials: "include",
            headers: { "Content-Type": "application/json" },
            body: JSON.stringify({ ids }),
        });
        if (!res.ok) throw new Error(await res.text());
        const blob = await res.blob();
        const url = URL.createObjectURL(blob);
        const a = document.createElement("a");
        a.href = url;
        a.download = `mrs-black-in-love-portfolio-${ids.length}.pdf`;
        document.body.appendChild(a); a.click(); a.remove();
        URL.revokeObjectURL(url);
        toast.success(`Portfolio (${ids.length}) downloaded.`);
    } catch { toast.error("Portfolio export failed"); }
}

async function downloadApplicationPdf(app) {
    try {
        const res = await fetch(`${BACKEND_URL}/api/admin/applications/${app.id}/pdf`, { credentials: "include" });
        if (!res.ok) throw new Error(await res.text());
        const blob = await res.blob();
        const url = URL.createObjectURL(blob);
        const a = document.createElement("a");
        a.href = url;
        a.download = `${app.first_name}-${app.last_name}-application.pdf`.replace(/\s+/g, "-");
        document.body.appendChild(a); a.click(); a.remove();
        URL.revokeObjectURL(url);
        toast.success("PDF downloaded.");
    } catch { toast.error("PDF download failed"); }
}

function Field({ label, value, mono = false, testid }) {
    if (value === null || value === undefined || value === "" || (Array.isArray(value) && value.length === 0)) return null;
    let display = value;
    if (typeof value === "boolean") display = value ? "Yes" : "No";
    if (Array.isArray(value)) display = value.join(", ");
    return (
        <div className="border-b border-[color:var(--espresso)]/50 py-3" data-testid={testid}>
            <p className="text-[0.6rem] uppercase tracking-[0.24em] text-[color:var(--cream-muted)]">{label}</p>
            <p className={`mt-1 text-sm text-[color:var(--soft-cream)] ${mono ? "font-mono" : ""} whitespace-pre-wrap break-words`}>{String(display)}</p>
        </div>
    );
}

function ApplicationDetail({ app, onStatus, onEdit, onRefresh, allTags = [] }) {
    const [lightboxIndex, setLightboxIndex] = useState(null);
    useEffect(() => { setLightboxIndex(null); }, [app?.id]);
    if (!app) {
        return (
            <div className="border border-[color:var(--espresso)] p-14 flex items-center justify-center min-h-[420px]" data-testid="app-detail-empty">
                <p className="font-serif italic text-[color:var(--cream-muted)] text-lg text-center max-w-sm">
                    Select an applicant on the left to read their entire submission here.
                </p>
            </div>
        );
    }
    const photos = app.photo_keys || [];
    return (
        <div className="border border-[color:var(--espresso)] p-8" data-testid={`app-detail-${app.id}`}>
            <div className="flex items-start justify-between gap-6 pb-6 border-b border-[color:var(--espresso)]">
                <div>
                    <p className="eyebrow">Application · {new Date(app.created_at).toLocaleDateString()}</p>
                    <h2 className="font-display text-4xl mt-2">{app.first_name} {app.last_name}</h2>
                    <p className="mt-2 text-sm text-[color:var(--cream-muted)]">
                        {app.city}{app.state ? `, ${app.state}` : ""} · {app.age} · {app.pronouns}
                    </p>
                    {(app.referral_code || app.referred_by_code) && (
                        <div className="mt-3 flex flex-wrap items-center gap-2 text-[0.65rem] uppercase tracking-[0.18em]" data-testid={`detail-referral-${app.id}`}>
                            {app.referral_code && (
                                <span className="border border-[color:var(--gold)]/60 bg-[color:var(--gold)]/5 text-[color:var(--gold)] px-2 py-1">
                                    Invite: <code className="font-mono">{app.referral_code}</code>
                                </span>
                            )}
                            {app.referred_by_code && (
                                <span className="border border-[color:var(--espresso)] text-[color:var(--cream-muted)] px-2 py-1">
                                    Referred by <code className="font-mono text-[color:var(--soft-cream)]">{app.referred_by_code}</code>
                                </span>
                            )}
                            {app.perk_unlocked_at && (
                                <span className="border border-[color:var(--gold)] bg-[color:var(--gold)]/10 text-[color:var(--gold)] px-2 py-1">Perk unlocked</span>
                            )}
                        </div>
                    )}
                </div>
                <span className="chip active" data-testid={`detail-status-${app.id}`}>{app.status}</span>
            </div>

            {app.video_delivery_pending && !app.video_key && (
                <div className="mt-5 border border-[color:var(--gold)] bg-[color:var(--gold)]/10 p-4" data-testid={`detail-video-pending-${app.id}`}>
                    <p className="text-[0.62rem] uppercase tracking-[0.24em] text-[color:var(--gold)]">Video pending — send-later acknowledged</p>
                    <p className="text-sm text-[color:var(--soft-cream)] mt-2">This applicant checked the "I'll text or email my video" box at submit. Watch <a href="mailto:casting@mrsblackinlove.org" className="link-underline text-[color:var(--gold)]">casting@mrsblackinlove.org</a> and <strong>380-200-0084</strong> for their video, then upload it via <em>Request photo re-upload</em> or attach it manually. They committed to sending within 24 hours of submitting.</p>
                </div>
            )}

            <div className="mt-6 flex flex-wrap gap-2">
                <button onClick={() => onStatus(app.id, "reviewing")} className="btn-gold text-[0.65rem]" data-testid={`detail-review-${app.id}`}>Mark reviewing</button>
                <button onClick={() => onStatus(app.id, "shortlist")} className="btn-gold text-[0.65rem]" data-testid={`detail-shortlist-${app.id}`}>Shortlist</button>
                <button onClick={() => onStatus(app.id, "callback")} className="btn-gold text-[0.65rem]" data-testid={`detail-callback-${app.id}`}>Callback</button>
                <button onClick={() => onStatus(app.id, "callback_2")} className="btn-gold text-[0.65rem]" data-testid={`detail-callback-2-${app.id}`} title="Send the second-callback email (Mixer + pricing + final-cast conversation) with your Calendly link">Callback 2</button>
                <button onClick={() => onStatus(app.id, "accepted")} className="btn-gold btn-gold-solid text-[0.65rem]" data-testid={`detail-accept-${app.id}`}>Accept</button>
                <button onClick={() => onStatus(app.id, "declined")} className="btn-gold text-[0.65rem]" data-testid={`detail-decline-${app.id}`}>Decline</button>
                <button
                    onClick={async () => {
                        if (!window.confirm(`Send ${app.first_name} an email asking them to re-upload their photos?`)) return;
                        try {
                            await api.post(`/admin/applications/${app.id}/request-reupload`);
                            toast.success(`Re-upload request sent to ${app.email}.`);
                        } catch { toast.error("Couldn't send the re-upload request."); }
                    }}
                    className="btn-gold text-[0.65rem]"
                    title="Emails the applicant a private link to add photos again — used after storage migrations."
                    data-testid={`detail-reupload-${app.id}`}
                >Request photo re-upload</button>
                <button onClick={() => downloadApplicationPdf(app)} className="btn-gold text-[0.65rem]" data-testid={`detail-pdf-${app.id}`}>
                    <Download size={12} /> Download PDF
                </button>
                {app.status === "accepted" && (
                    <button onClick={() => onEdit(app)} className="btn-gold text-[0.65rem]" data-testid={`detail-edit-profile-${app.id}`}>Edit cast profile</button>
                )}
                {app.private_token && (
                    <a href={`/apply/private/${app.private_token}`} target="_blank" rel="noreferrer" className="btn-gold text-[0.65rem]" data-testid={`detail-private-link-${app.id}`}>Open private link →</a>
                )}
            </div>

            <section className="mt-8" data-testid={`detail-photos-section-${app.id}`}>
                <div className="flex items-baseline justify-between">
                    <p className="eyebrow">Uploaded photos ({photos.length})</p>
                    {photos.length > 0 && (
                        <span className="text-[0.6rem] uppercase tracking-[0.24em] text-[color:var(--cream-muted)]">Hover a photo to reorder · first is primary</span>
                    )}
                </div>
                {photos.length === 0 ? (
                    <div className="mt-3 border border-dashed border-[color:var(--espresso)] p-6 text-center" data-testid={`detail-no-photos-${app.id}`}>
                        <p className="text-sm text-[color:var(--cream-muted)]">This applicant did not upload any photos. Their video and social links are below.</p>
                    </div>
                ) : (
                    <div className="mt-3 grid grid-cols-3 md:grid-cols-4 gap-2">
                        {photos.map((k, i) => {
                            const move = async (dir) => {
                                const to = i + dir;
                                if (to < 0 || to >= photos.length) return;
                                const next = [...photos];
                                [next[i], next[to]] = [next[to], next[i]];
                                try { await reorderPhotos(app.id, next); onRefresh?.(); }
                                catch { toast.error("Reorder failed"); }
                            };
                            const promote = async () => {
                                if (i === 0) return;
                                const next = [photos[i], ...photos.filter((_, x) => x !== i)];
                                try { await reorderPhotos(app.id, next); onRefresh?.(); toast.success("Set as primary"); }
                                catch { toast.error("Reorder failed"); }
                            };
                            return (
                                <div key={k} className="relative group aspect-square" data-testid={`photo-tile-${i}`}>
                                    <button onClick={() => setLightboxIndex(i)} className="w-full h-full border border-[color:var(--espresso)] hover:border-[color:var(--gold)] overflow-hidden block" data-testid={`detail-photo-${k}`}>
                                        <img src={`${BACKEND_URL}/api/uploads/${k}`} alt="applicant" className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500" />
                                    </button>
                                    {i === 0 && (
                                        <span className="absolute top-1 left-1 bg-[color:var(--gold)] text-[color:var(--warm-black)] text-[0.55rem] uppercase tracking-[0.2em] px-2 py-0.5 font-bold pointer-events-none" data-testid={`photo-primary-badge-${i}`}>Primary</span>
                                    )}
                                    <div className="absolute inset-x-1 bottom-1 flex gap-1 opacity-0 group-hover:opacity-100 transition-opacity">
                                        <button onClick={(e) => { e.stopPropagation(); move(-1); }} disabled={i === 0} className="flex-1 bg-black/80 text-white text-xs py-1 hover:bg-[color:var(--gold)] hover:text-black disabled:opacity-30 disabled:hover:bg-black/80 disabled:hover:text-white" data-testid={`photo-move-left-${i}`} aria-label="Move left"><ChevronLeft size={12} className="mx-auto" /></button>
                                        {i !== 0 && (
                                            <button onClick={(e) => { e.stopPropagation(); promote(); }} className="flex-1 bg-black/80 text-[color:var(--gold)] text-xs py-1 hover:bg-[color:var(--gold)] hover:text-black" data-testid={`photo-promote-${i}`} aria-label="Set as primary"><Star size={12} className="mx-auto" /></button>
                                        )}
                                        <button onClick={(e) => { e.stopPropagation(); move(1); }} disabled={i === photos.length - 1} className="flex-1 bg-black/80 text-white text-xs py-1 hover:bg-[color:var(--gold)] hover:text-black disabled:opacity-30 disabled:hover:bg-black/80 disabled:hover:text-white" data-testid={`photo-move-right-${i}`} aria-label="Move right"><ChevronRight size={12} className="mx-auto" /></button>
                                    </div>
                                </div>
                            );
                        })}
                    </div>
                )}
            </section>

            <section className="mt-8">
                <p className="eyebrow">Contact</p>
                <div className="mt-2 grid md:grid-cols-2 gap-x-8">
                    <Field label="Email" value={app.email} mono />
                    <Field label="Phone" value={app.phone} mono />
                </div>
            </section>

            <section className="mt-8">
                <p className="eyebrow">Identity</p>
                <div className="mt-2 grid md:grid-cols-2 gap-x-8">
                    <Field label="Pronouns" value={app.pronouns} />
                    <Field label="Age" value={app.age} />
                    <Field label="Occupation" value={app.occupation} />
                    <Field label="Identity note" value={app.identity_note} />
                    <Field label="Relationship status" value={app.relationship_status} />
                </div>
            </section>

            {(app.partner_name || app.partner_included !== undefined || app.years_together || app.dating_dynamic || app.single_intent || app.partner_applying !== undefined) && (
                <section className="mt-8">
                    <p className="eyebrow">Relationship details</p>
                    <div className="mt-2 grid md:grid-cols-2 gap-x-8">
                        <Field label="Partner name" value={app.partner_name} />
                        <Field label="Partner included in filming?" value={app.partner_included} />
                        <Field label="Years together" value={app.years_together} />
                        <Field label="Dating dynamic" value={app.dating_dynamic} />
                        <Field label="What they're hoping for (single)" value={app.single_intent} />
                        <Field label="Partner also applying?" value={app.partner_applying} />
                        <Field label="Acknowledged solo submission" value={app.consent_solo_submission} />
                    </div>
                </section>
            )}

            <section className="mt-8">
                <p className="eyebrow">Their story</p>
                <Field label="Q1 · Tell us about yourself" value={app.describe_yourself} />
                <Field label="Q2 · What people often misunderstand about them" value={app.misunderstood_by_others} />
                <Field label="Q3 · Last disagreement (what happened, how they handled it, what they learned)" value={app.conflict_style} />
                <Field label="Q4 · Why they want to be part of this project" value={app.community_meaning} />
                <Field label="Q5 · What they hope the cast says about them" value={app.cast_impression} />
            </section>

            <section className="mt-8">
                <p className="eyebrow">On camera & availability</p>
                <div className="mt-2 grid md:grid-cols-2 gap-x-8">
                    <Field label="On-camera comfort" value={app.on_camera_comfort} />
                    <Field label="Available Sept 25–27" value={app.available_sep_25_27} />
                    <Field label="Willing to drive 2–3 hours" value={app.willing_to_drive_2_3_hours} />
                    <Field label="Availability notes (legacy)" value={app.availability} />
                </div>
            </section>

            {(app.facebook || app.instagram || app.tiktok || app.other_socials || app.referral || app.video_url || app.video_key) && (
                <section className="mt-8">
                    <p className="eyebrow">Socials, referral & video</p>
                    <div className="mt-2 grid md:grid-cols-2 gap-x-8">
                        {app.facebook && <Field label="Facebook" value={app.facebook} mono />}
                        {app.instagram && <Field label="Instagram" value={app.instagram.startsWith("@") ? app.instagram : `@${app.instagram}`} mono />}
                        {app.tiktok && <Field label="TikTok" value={app.tiktok.startsWith("@") ? app.tiktok : `@${app.tiktok}`} mono />}
                        <Field label="Other socials" value={app.other_socials} mono />
                        <Field label="How they heard about us" value={app.referral} />
                    </div>
                    {app.video_url && (
                        <div className="mt-3">
                            <p className="text-[0.6rem] uppercase tracking-[0.24em] text-[color:var(--cream-muted)]">Video (link)</p>
                            <a href={app.video_url} target="_blank" rel="noreferrer" className="link-underline text-[color:var(--gold)] text-sm break-all" data-testid={`detail-video-${app.id}`}>{app.video_url}</a>
                        </div>
                    )}
                    {app.video_key && (
                        <div className="mt-3">
                            <p className="text-[0.6rem] uppercase tracking-[0.24em] text-[color:var(--cream-muted)]">Video (uploaded)</p>
                            <video src={`${BACKEND_URL}/api/uploads/${app.video_key}`} controls className="mt-2 max-w-md w-full border border-[color:var(--espresso)]" data-testid={`detail-video-file-${app.id}`} />
                            <a href={`${BACKEND_URL}/api/uploads/${app.video_key}`} target="_blank" rel="noreferrer" className="link-underline text-[color:var(--gold)] text-xs block mt-2">Open in new tab →</a>
                        </div>
                    )}
                </section>
            )}

            <section className="mt-8">
                <p className="eyebrow">Consents</p>
                <div className="mt-2 grid md:grid-cols-3 gap-x-8">
                    <Field label="Share materials" value={app.consent_share} />
                    <Field label="Consent to filming" value={app.consent_filming} />
                    <Field label="Truthful submission" value={app.consent_truth} />
                </div>
            </section>

            <TagManager app={app} onChanged={onRefresh} allTags={allTags} />
            <CastingNotes app={app} onSaved={onRefresh} />

            <section className="mt-8 pt-6 border-t border-[color:var(--espresso)]">
                <p className="eyebrow">Meta</p>
                <div className="mt-2 grid md:grid-cols-2 gap-x-8">
                    <Field label="Application ID" value={app.id} mono />
                    <Field label="Submitted" value={new Date(app.created_at).toLocaleString()} />
                </div>
            </section>

            <PhotoLightbox photos={photos} index={lightboxIndex} onClose={() => setLightboxIndex(null)} onIndex={setLightboxIndex} />
        </div>
    );
}

const BOARD_COLUMNS = [
    { id: "submitted", label: "Submitted" },
    { id: "reviewing", label: "Reviewing" },
    { id: "shortlist", label: "Shortlist" },
    { id: "callback", label: "Callback" },
    { id: "callback_2", label: "Callback 2" },
    { id: "accepted", label: "Accepted" },
    { id: "declined", label: "Declined" },
];

function ShortlistBoard({ apps, onStatus, onCardClick }) {
    const [dragId, setDragId] = useState(null);
    const [overCol, setOverCol] = useState(null);

    const drop = async (colId) => {
        setOverCol(null);
        if (!dragId) return;
        const app = apps.find((a) => a.id === dragId);
        if (!app || app.status === colId) { setDragId(null); return; }
        try {
            await onStatus(dragId, colId);
            toast.success(`Moved to ${colId}`);
        } catch { toast.error("Move failed"); }
        setDragId(null);
    };

    return (
        <div className="grid grid-cols-2 lg:grid-cols-5 gap-4" data-testid="shortlist-board">
            {BOARD_COLUMNS.map((col) => {
                const colApps = apps.filter((a) => a.status === col.id);
                return (
                    <div
                        key={col.id}
                        onDragOver={(e) => { e.preventDefault(); setOverCol(col.id); }}
                        onDragLeave={() => setOverCol((c) => (c === col.id ? null : c))}
                        onDrop={() => drop(col.id)}
                        className={"border p-3 min-h-[500px] transition-colors " + (overCol === col.id ? "border-[color:var(--gold)] bg-[color:var(--gold)]/5" : "border-[color:var(--espresso)]")}
                        data-testid={`board-column-${col.id}`}
                    >
                        <div className="flex items-baseline justify-between mb-3 pb-2 border-b border-[color:var(--espresso)]">
                            <p className="eyebrow">{col.label}</p>
                            <span className="text-[0.6rem] uppercase tracking-[0.24em] text-[color:var(--cream-muted)]" data-testid={`board-count-${col.id}`}>{colApps.length}</span>
                        </div>
                        <div className="space-y-2">
                            {colApps.map((a) => (
                                <div
                                    key={a.id}
                                    draggable
                                    onDragStart={() => setDragId(a.id)}
                                    onDragEnd={() => setDragId(null)}
                                    onClick={() => onCardClick?.(a.id)}
                                    className={"border border-[color:var(--espresso)] p-3 cursor-grab active:cursor-grabbing hover:border-[color:var(--gold)] bg-[color:var(--warm-black)] transition-all " + (dragId === a.id ? "opacity-40" : "")}
                                    data-testid={`board-card-${a.id}`}
                                >
                                    <p className="font-serif text-sm text-[color:var(--soft-cream)] truncate">{a.first_name} {a.last_name}</p>
                                    <p className="text-[0.65rem] text-[color:var(--cream-muted)] truncate mt-0.5">{a.city}{a.age ? ` · ${a.age}` : ""}</p>
                                    {a.tags && a.tags.length > 0 && (
                                        <div className="mt-2 flex flex-wrap gap-1">
                                            {a.tags.slice(0, 3).map((t) => (
                                                <span key={t.id} className="text-[0.5rem] uppercase tracking-[0.18em] px-1.5 py-0.5 border" style={{ borderColor: t.color, color: t.color }}>
                                                    {t.label}
                                                </span>
                                            ))}
                                        </div>
                                    )}
                                </div>
                            ))}
                            {colApps.length === 0 && (
                                <p className="text-[0.65rem] italic text-[color:var(--cream-muted)]/50 text-center py-6">Drop here</p>
                            )}
                        </div>
                    </div>
                );
            })}
        </div>
    );
}

export default function ApplicationsPanel({ apps, selectedId, onSelect, onStatus, onEdit, onRefresh }) {
    const [filter, setFilter] = useState("all");
    const [query, setQuery] = useState("");
    const [occupationQuery, setOccupationQuery] = useState("");
    const [compareMode, setCompareMode] = useState(false);
    const [compareIds, setCompareIds] = useState([]);
    const [viewMode, setViewMode] = useState("list");
    const [activeTags, setActiveTags] = useState([]);
    const STATUSES = ["all", "submitted", "reviewing", "shortlist", "callback", "callback_2", "accepted", "declined"];
    const allTags = (() => {
        const seen = new Map();
        for (const a of apps) for (const t of (a.tags || [])) if (!seen.has(t.label)) seen.set(t.label, t.color);
        return [...seen.entries()].map(([label, color]) => ({ label, color }));
    })();
    const filtered = apps.filter((a) => {
        if (filter !== "all" && a.status !== filter) return false;
        if (activeTags.length && !(a.tags || []).some((t) => activeTags.includes(t.label))) return false;
        if (query) {
            const q = query.toLowerCase();
            const hay = `${a.first_name} ${a.last_name} ${a.email} ${a.city} ${a.pronouns || ""} ${a.occupation || ""}`.toLowerCase();
            if (!hay.includes(q)) return false;
        }
        if (occupationQuery) {
            const oq = occupationQuery.toLowerCase().trim();
            if (!oq) return true;
            if (!(a.occupation || "").toLowerCase().includes(oq)) return false;
        }
        return true;
    });
    const selected = filtered.find((a) => a.id === selectedId) || apps.find((a) => a.id === selectedId);
    const comparing = compareMode && compareIds.length === 2;
    const compareApps = compareIds.map((id) => apps.find((a) => a.id === id)).filter(Boolean);

    const handleRow = (id) => {
        if (compareMode) {
            setCompareIds((prev) => {
                if (prev.includes(id)) return prev.filter((x) => x !== id);
                if (prev.length >= 2) return [prev[1], id];
                return [...prev, id];
            });
        } else {
            onSelect(id);
        }
    };

    const toggleCompare = () => {
        setCompareMode((v) => {
            if (v) setCompareIds([]);
            return !v;
        });
    };

    const toggleTagFilter = (label) => {
        setActiveTags((prev) => (prev.includes(label) ? prev.filter((x) => x !== label) : [...prev, label]));
    };

    return (
        <div data-testid="applications-panel">
            <div className="flex items-center justify-between mb-6">
                <div className="inline-flex border border-[color:var(--espresso)]" data-testid="view-mode-toggle">
                    <button
                        onClick={() => setViewMode("list")}
                        className={"text-[0.65rem] uppercase tracking-[0.24em] px-4 py-2 " + (viewMode === "list" ? "bg-[color:var(--gold)] text-[color:var(--warm-black)]" : "text-[color:var(--cream-muted)] hover:text-[color:var(--gold)]")}
                        data-testid="view-list"
                    >List</button>
                    <button
                        onClick={() => setViewMode("board")}
                        className={"text-[0.65rem] uppercase tracking-[0.24em] px-4 py-2 " + (viewMode === "board" ? "bg-[color:var(--gold)] text-[color:var(--warm-black)]" : "text-[color:var(--cream-muted)] hover:text-[color:var(--gold)]")}
                        data-testid="view-board"
                    >Board</button>
                </div>
                <span className="text-[0.65rem] uppercase tracking-[0.24em] text-[color:var(--cream-muted)]">{filtered.length} of {apps.length}</span>
            </div>

            {viewMode === "board" ? (
                <ShortlistBoard apps={filtered} onStatus={onStatus} onCardClick={(id) => { setViewMode("list"); onSelect(id); }} />
            ) : (
                <div className="grid lg:grid-cols-12 gap-6">
                    <aside className={"border border-[color:var(--espresso)] " + (comparing ? "lg:col-span-3" : "lg:col-span-4")}>
                        <div className="p-4 border-b border-[color:var(--espresso)] space-y-3">
                            <input className="input-line w-full" placeholder="Search name, email, city, occupation…" value={query} onChange={(e) => setQuery(e.target.value)} data-testid="apps-search" />
                            <input className="input-line w-full" placeholder="Filter by occupation (nurse, founder, teacher…)" value={occupationQuery} onChange={(e) => setOccupationQuery(e.target.value)} data-testid="apps-occupation-filter" />
                            <div className="flex flex-wrap gap-1">
                                {STATUSES.map((s) => (
                                    <button
                                        key={s}
                                        onClick={() => setFilter(s)}
                                        data-testid={`apps-filter-${s}`}
                                        className={
                                            "text-[0.6rem] uppercase tracking-[0.24em] px-3 py-1 border transition-colors duration-300 " +
                                            (filter === s
                                                ? "bg-[color:var(--gold)] border-[color:var(--gold)] text-[color:var(--warm-black)]"
                                                : "border-[color:var(--espresso)] text-[color:var(--cream-muted)] hover:border-[color:var(--gold)] hover:text-[color:var(--gold)]")
                                        }
                                    >
                                        {s}
                                    </button>
                                ))}
                            </div>

                            {allTags.length > 0 && (
                                <div className="pt-2 border-t border-[color:var(--espresso)]/50" data-testid="apps-tag-filter">
                                    <p className="text-[0.55rem] uppercase tracking-[0.24em] text-[color:var(--cream-muted)] mb-2">Filter by tag</p>
                                    <div className="flex flex-wrap gap-1">
                                        {allTags.map((t) => {
                                            const active = activeTags.includes(t.label);
                                            return (
                                                <button
                                                    key={t.label}
                                                    onClick={() => toggleTagFilter(t.label)}
                                                    data-testid={`apps-tag-chip-${t.label}`}
                                                    className="text-[0.55rem] uppercase tracking-[0.2em] px-2 py-1 border transition-all"
                                                    style={{
                                                        background: active ? t.color : "transparent",
                                                        borderColor: t.color,
                                                        color: active ? "#0f0a09" : t.color,
                                                    }}
                                                >
                                                    {t.label}
                                                </button>
                                            );
                                        })}
                                    </div>
                                </div>
                            )}

                            <div className="flex gap-2 pt-1">
                                <button onClick={() => bulkExportPortfolio(filtered.map((a) => a.id))} disabled={filtered.length === 0} className="flex-1 btn-gold btn-gold-solid text-[0.6rem] justify-center disabled:opacity-30" data-testid="apps-bulk-export">
                                    <Download size={11} /> Export {filtered.length} PDF
                                </button>
                                <button onClick={toggleCompare} className={"btn-gold text-[0.6rem] " + (compareMode ? "bg-[color:var(--gold)] text-[color:var(--warm-black)]" : "")} data-testid="apps-compare-toggle">
                                    <GitCompare size={11} /> {compareMode ? "Exit" : "Compare"}
                                </button>
                            </div>
                            {compareMode && (
                                <p className="text-[0.6rem] uppercase tracking-[0.24em] text-[color:var(--gold)]" data-testid="compare-hint">
                                    Compare mode · pick two applicants ({compareIds.length}/2 selected)
                                </p>
                            )}
                        </div>
                        <div className="max-h-[70vh] overflow-y-auto" data-testid="apps-list">
                            {filtered.map((a) => {
                                const active = compareMode ? compareIds.includes(a.id) : a.id === selectedId;
                                return (
                                    <button
                                        key={a.id}
                                        onClick={() => handleRow(a.id)}
                                        className={
                                            "w-full text-left px-5 py-4 border-b border-[color:var(--espresso)]/50 transition-colors duration-200 " +
                                            (active
                                                ? "bg-[color:var(--gold)]/10 border-l-2 border-l-[color:var(--gold)]"
                                                : "hover:bg-[color:var(--dark-grey)]")
                                        }
                                        data-testid={`apps-row-${a.id}`}
                                    >
                                        <div className="flex items-center justify-between gap-3">
                                            <div className="flex items-center gap-2 min-w-0">
                                                {compareMode && (
                                                    <span className={"w-3 h-3 border shrink-0 " + (active ? "bg-[color:var(--gold)] border-[color:var(--gold)]" : "border-[color:var(--gold)]/50")} data-testid={`apps-checkbox-${a.id}`} />
                                                )}
                                                <p className={"font-serif text-lg truncate " + (active ? "text-[color:var(--gold)]" : "text-[color:var(--soft-cream)]")}>
                                                    {a.first_name} {a.last_name}
                                                </p>
                                                {a.video_delivery_pending && !a.video_key && (
                                                    <span
                                                        className="inline-flex items-center shrink-0 text-[0.52rem] uppercase tracking-[0.18em] px-1.5 py-0.5 border border-[color:var(--gold)] text-[color:var(--gold)] bg-[color:var(--gold)]/10"
                                                        title="Applicant checked the send-later box — video is coming via text or email"
                                                        data-testid={`apps-row-video-pending-${a.id}`}
                                                    >Video pending</span>
                                                )}
                                            </div>
                                            <span className="text-[0.55rem] uppercase tracking-[0.24em] text-[color:var(--cream-muted)] shrink-0">{a.status}</span>
                                        </div>
                                        <p className="text-xs text-[color:var(--cream-muted)] mt-1 truncate">{a.city}{a.relationship_status ? ` · ${a.relationship_status}` : ""}</p>
                                        <p className="text-[0.65rem] text-[color:var(--cream-muted)]/70 mt-1 truncate">{a.email}</p>
                                        {a.tags && a.tags.length > 0 && (
                                            <div className="mt-2 flex flex-wrap gap-1">
                                                {a.tags.slice(0, 4).map((t) => (
                                                    <span key={t.id} className="text-[0.5rem] uppercase tracking-[0.18em] px-1.5 py-0.5 border" style={{ borderColor: t.color, color: t.color }}>
                                                        {t.label}
                                                    </span>
                                                ))}
                                                {a.tags.length > 4 && <span className="text-[0.5rem] text-[color:var(--cream-muted)]">+{a.tags.length - 4}</span>}
                                            </div>
                                        )}
                                    </button>
                                );
                            })}
                            {filtered.length === 0 && (
                                <p className="p-6 text-sm text-[color:var(--cream-muted)]" data-testid="apps-empty">No applicants match this filter.</p>
                            )}
                        </div>
                        <div className="px-4 py-3 border-t border-[color:var(--espresso)] text-[0.65rem] uppercase tracking-[0.24em] text-[color:var(--cream-muted)]">
                            Showing {filtered.length} of {apps.length}
                        </div>
                    </aside>

                    <div className={comparing ? "lg:col-span-9" : "lg:col-span-8"}>
                        {comparing ? (
                            <div className="grid md:grid-cols-2 gap-4" data-testid="applications-compare">
                                {compareApps.map((a) => (
                                    <ApplicationDetail key={a.id} app={a} onStatus={onStatus} onEdit={onEdit} onRefresh={onRefresh} allTags={allTags} />
                                ))}
                            </div>
                        ) : (
                            <ApplicationDetail app={selected} onStatus={onStatus} onEdit={onEdit} onRefresh={onRefresh} allTags={allTags} />
                        )}
                    </div>
                </div>
            )}
        </div>
    );
}
